import { React, useEffect, useMemo, useState } from 'react';

import { Box, Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useNavigate } from 'react-router-dom';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import { formatDatePtBr, getDaysUntilDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/fireExtinguishersBI.css';

const expirationWindowDays = 30;

const getStatus = (validade) => {
    const daysUntilExpiration = getDaysUntilDate(validade);

    if (daysUntilExpiration < 0) {
        return {
            className: 'is-expired',
            icon: <ReportProblemRoundedIcon />,
            label: 'Vencido'
        };
    }

    if (daysUntilExpiration <= expirationWindowDays) {
        return {
            className: 'is-warning',
            icon: <WarningAmberRoundedIcon />,
            label: 'A vencer'
        };
    }

    return {
        className: 'is-ok',
        icon: <TaskAltRoundedIcon />,
        label: 'Vigente'
    };
};

const ExtintoresBI = () => {
    const navigate = useNavigate();
    const [extintores, setExtintores] = useState([]);
    const [selectedSite, setSelectedSite] = useState('all');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        async function fetchReportData () {
            const extintoresResponse = await fetchFlask('listarExtintores');
            if (extintoresResponse.status === 'error') {
                setMessage(extintoresResponse.message);
                setStatus(extintoresResponse.status);
                setOpenAlerta(true);
                return;
            }

            setExtintores(extintoresResponse.extintores);
        }

        fetchReportData();
    }, []);

    const sites = useMemo(() => [...new Set(extintores.map((extintor) => extintor.site))], [extintores]);

    const filteredExtintores = useMemo(() => {
        if (selectedSite === 'all') {
            return extintores;
        }

        return extintores.filter((extintor) => extintor.site === selectedSite);
    }, [extintores, selectedSite]);

    const kpis = useMemo(() => {
        const vencidos = filteredExtintores.filter((extintor) => getDaysUntilDate(extintor.validade) < 0).length;
        const aVencer = filteredExtintores.filter((extintor) => {
            const daysUntilExpiration = getDaysUntilDate(extintor.validade);
            return daysUntilExpiration >= 0 && daysUntilExpiration <= expirationWindowDays;
        }).length;
        const vigentes = filteredExtintores.length - vencidos - aVencer;

        return {
            total: filteredExtintores.length,
            vencidos,
            aVencer,
            vigentes
        };
    }, [filteredExtintores]);

    const selectedSiteLabel = selectedSite === 'all'
        ? 'Todos os sites'
        : selectedSite;
    const visibleSiteCount = selectedSite === 'all' ? sites.length : 1;
    const okPercent = kpis.total > 0 ? (kpis.vigentes / kpis.total) * 100 : 0;
    const warningPercent = kpis.total > 0 ? (kpis.aVencer / kpis.total) * 100 : 0;
    const expiredPercent = kpis.total > 0 ? (kpis.vencidos / kpis.total) * 100 : 0;

    return (
        <Box className='mainBox extinguishers-bi-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Extintores</PageHeader>

            <Box className='extinguishers-bi-shell'>
                <Box className='extinguishers-bi-hero'>
                    <Box>
                        <h2>Extintores Setta Serviços</h2>
                        <p>Visão consolidada de validade, fornecedor, tipo e observações por site.</p>
                    </Box>

                    <Box className='extinguishers-bi-actions'>
                        <FormControl className='extinguishers-bi-site-filter'>
                            <InputLabel id='labelSiteExtintoresBI'>Site</InputLabel>
                            <Select
                                labelId='labelSiteExtintoresBI'
                                label='Site'
                                value={selectedSite}
                                onChange={(event) => setSelectedSite(event.target.value)}
                            >
                                <MenuItem value='all'>Todos</MenuItem>
                                {sites.map((site) => (
                                    <MenuItem key={site} value={site}>
                                        {site}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant='contained'
                            startIcon={<LocalFireDepartmentRoundedIcon />}
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={() => navigate('/OeM/atualizarExtintores')}
                        >
                            Atualizar Extintores
                        </Button>
                    </Box>
                </Box>

                <Box className='extinguishers-bi-kpi-grid'>
                    <Box className='extinguishers-bi-kpi is-primary'>
                        <LocalFireDepartmentRoundedIcon />
                        <Box>
                            <span>Total filtrado</span>
                            <strong>{kpis.total}</strong>
                        </Box>
                    </Box>

                    <Box className='extinguishers-bi-kpi'>
                        <WarningAmberRoundedIcon />
                        <Box>
                            <span>A vencer em 30 dias</span>
                            <strong>{kpis.aVencer}</strong>
                        </Box>
                    </Box>

                    <Box className='extinguishers-bi-kpi'>
                        <ReportProblemRoundedIcon />
                        <Box>
                            <span>Vencidos</span>
                            <strong>{kpis.vencidos}</strong>
                        </Box>
                    </Box>

                    <Box className='extinguishers-bi-health'>
                        <Box className='extinguishers-bi-health-heading'>
                            <InsightsRoundedIcon />
                            <Box>
                                <span>Distribuição</span>
                                <strong>{selectedSiteLabel}</strong>
                            </Box>
                        </Box>
                        <Box className='extinguishers-bi-health-bar'>
                            <span className='is-ok' style={{ width: `${okPercent}%` }} />
                            <span className='is-warning' style={{ width: `${warningPercent}%` }} />
                            <span className='is-expired' style={{ width: `${expiredPercent}%` }} />
                        </Box>
                        <Box className='extinguishers-bi-health-legend'>
                            <span><i className='is-ok' /> Vigentes: {kpis.vigentes}</span>
                            <span><i className='is-warning' /> A vencer: {kpis.aVencer}</span>
                            <span><i className='is-expired' /> Vencidos: {kpis.vencidos}</span>
                        </Box>
                    </Box>
                </Box>

                <Box className='extinguishers-bi-table-panel'>
                    <Box className='extinguishers-bi-table-toolbar'>
                        <Box>
                            <span><FilterAltRoundedIcon /> Filtro ativo</span>
                            <strong>{selectedSiteLabel}</strong>
                        </Box>
                        <Box>
                            <span><FactCheckRoundedIcon /> Sites</span>
                            <strong>{visibleSiteCount}</strong>
                        </Box>
                    </Box>

                    <Box className='extinguishers-bi-table-scroll'>
                        <table className='extinguishers-bi-table'>
                            <thead>
                                <tr>
                                    <th>Site</th>
                                    <th>Fornecedor</th>
                                    <th>Tipo</th>
                                    <th>Validade</th>
                                    <th>Status</th>
                                    <th>Observação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExtintores.map((extintor) => {
                                    const itemStatus = getStatus(extintor.validade);
                                    return (
                                        <tr key={extintor.extintor_id}>
                                            <td>{extintor.site}</td>
                                            <td>{extintor.fornecedor}</td>
                                            <td>{extintor.tipo}</td>
                                            <td>{formatDatePtBr(extintor.validade)}</td>
                                            <td>
                                                <span className={`extinguishers-bi-status ${itemStatus.className}`}>
                                                    {itemStatus.icon}
                                                    {itemStatus.label}
                                                </span>
                                            </td>
                                            <td>{extintor.obs}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ExtintoresBI;
